import React, { useEffect, useState } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { ChevronDown } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import RegistrySheetExpanded from './RegistrySheetExpanded';
import { useHistory } from '@react-router';
import { useEventInfo } from '@shared/utils/eventInfo';
import { ClickableArea, DesktopPopup, ExpandedDesktopPopup, ExpandedMobileSheet, MobileSheet, Overlay, TitleGradient } from './RegistrySheet.styles';
import { useRegistrySheetTelemetry } from './RegistrySheet.telemetry';
import { Page } from '@apps/registry/common/components/RegistryGiftCounts/telemetry';

interface RegistrySheetProps {
  desktopPosition?: 'top-right' | 'bottom-right';
  page: Page;
}

const RegistrySheet: React.FC<RegistrySheetProps> = ({ desktopPosition = 'top-right', page }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const history = useHistory();
  const { eventHandle } = useEventInfo();
  const telemetry = useRegistrySheetTelemetry();
  const isMobile = useIsMobileOrTablet();
  const { t } = useTranslation('sharedRegistry');
  const { giftCountReminderTitle, giftCountReminderSubTitle } = t('registryPromo');

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleClick = () => {
    setIsExpanded(true);
  };

  const handleDismiss = () => {
    telemetry.registrySheetDismissed(page);
    setIsExpanded(false);
    setIsVisible(false);
  };

  const handleAddGifts = () => {
    telemetry.registrySheetAddGiftClicked(page);
    history.push(`/${eventHandle}/edit/registry/shop`);
  };

  const SheetComponent = !isMobile ? (props: React.ComponentProps<typeof DesktopPopup>) => <DesktopPopup {...props} position={desktopPosition} /> : MobileSheet;
  const ExpandedSheetComponent = !isMobile
    ? (props: React.ComponentProps<typeof ExpandedDesktopPopup>) => <ExpandedDesktopPopup {...props} position={desktopPosition} />
    : ExpandedMobileSheet;

  return (
    <>
      <Overlay isVisible={isVisible || isExpanded} isExpanded={isExpanded} onClick={handleDismiss} />
      <SheetComponent isVisible={isVisible && !isExpanded} backgroundColor="mono1">
        <Box padding={6}>
          <Flex alignItems="center" justifyContent="space-between">
            <Box flex="1" flexDirection="column" display="flex" justifyContent="center">
              <ClickableArea alignItems="center" onClick={handleClick} flexDirection="column" textAlign="center">
                <TextV2 typographyVariant="hed4" marginBottom={3}>
                  <TitleGradient>{giftCountReminderTitle()}</TitleGradient>
                </TextV2>
                <TextV2 typographyVariant="body1" color="mono12" marginBottom={3}>
                  {giftCountReminderSubTitle()}
                </TextV2>
                <ChevronDown />
              </ClickableArea>
            </Box>
          </Flex>
        </Box>
      </SheetComponent>
      <ExpandedSheetComponent isVisible={isExpanded}>
        <Box padding={6} backgroundColor="mono1" borderRadius={24}>
          <RegistrySheetExpanded handleDismiss={handleDismiss} handleAddGifts={handleAddGifts} page={page} />
        </Box>
      </ExpandedSheetComponent>
    </>
  );
};

export default RegistrySheet;
